import { useCallback, useEffect, useState } from "react";
import { captureError } from "@/lib/sentry";
import AppLayout from "@/components/AppLayout";
import { api, LEAGUES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarIcon } from "@phosphor-icons/react";

export default function Players() {
  const { user, setUser } = useAuth();
  const [players, setPlayers] = useState([]);
  const [league, setLeague] = useState("ALL");
  const [watch, setWatch] = useState(user?.favorite_players || []);
  const [saving, setSaving] = useState(false);

  const loadPlayers = useCallback(async (lg) => {
    try {
      const r = await api.get("/players", { params: { league: lg } });
      setPlayers(r.data || []);
    } catch (err) {
      captureError("failed to load players:", err);
    }
  }, []);

  useEffect(() => {
    loadPlayers(league);
  }, [loadPlayers, league]);

  useEffect(() => {
    setWatch(user?.favorite_players || []);
  }, [user]);

  const toggle = useCallback((id) => {
    setWatch((w) => (w.includes(id) ? w.filter((x) => x !== id) : [...w, id]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/auth/preferences", { favorite_players: watch });
      setUser(res.data);
    } catch (err) {
      captureError("failed to save players:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="mb-6 fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A84FF] mb-2">Watchlist</p>
        <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Player Watchlist</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {LEAGUES.map((lg) => (
          <button
            key={lg}
            onClick={() => setLeague(lg)}
            data-testid={`players-league-${lg}`}
            className={`px-4 py-2 text-xs font-black uppercase tracking-[0.15em] rounded-md border transition-colors ${
              league === lg ? "border-[#0A84FF] bg-[#0A84FF]/10 text-[#0A84FF]" : "border-white/10 text-white/60 hover:text-white"
            }`}
          >
            {lg}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {players.map((p) => {
          const active = watch.includes(p.id);
          return (
            <div key={p.id} className="p-5 rounded-md bg-[#121212] border border-white/10" data-testid={`player-${p.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">{p.league} · {p.team} · {p.position}</span>
                <button
                  data-testid={`watch-${p.id}`}
                  onClick={() => toggle(p.id)}
                  className={`text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 ${active ? "text-[#32D74B]" : "text-white/40 hover:text-white"}`}
                >
                  {active ? <StarIcon weight="fill" /> : <StarIcon />} {active ? "Watching" : "Watch"}
                </button>
              </div>
              <h3 className="font-display font-bold text-2xl uppercase tracking-tighter">{p.name}</h3>
              <p className="text-sm text-white/65 mt-2 mb-4">{p.storyline}</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                {Object.entries(p.stats).slice(0, 3).map(([k, v]) => (
                  <div key={k} className="rounded bg-black/40 border border-white/5 py-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">{k}</p>
                    <p className="font-num text-2xl font-black">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <button
        data-testid="save-players-btn"
        onClick={save}
        disabled={saving}
        className="px-6 py-3 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white font-bold uppercase tracking-wider text-sm"
      >
        {saving ? "Saving…" : "Save watchlist"}
      </button>
    </AppLayout>
  );
}
